import React, { useState, useCallback, useMemo } from 'react';
import { Card, Radio, DatePicker, Button, Table, Spin, Statistic, Alert, Tag, Popconfirm, message } from 'antd';
import { AuditOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { c901gApi, RunAuditRequest, AuditResult, RunAuditResponse } from '../../services/c901gApi';
import './C901GForm.css';

type AuditCategory = RunAuditRequest['category'];

const CATEGORY_OPTIONS: { value: AuditCategory; label: string; description: string; destructive: boolean }[] = [
  { value: 'amount_check',     label: '金額稽核',     description: '檢查主表金額與子表重算金額的差異', destructive: false },
  { value: 'blank_key_check',  label: '空白主鍵檢查', description: '掃描主鍵欄位為空的異常資料',       destructive: false },
  { value: 'warehouse_fix',    label: '慶佑倉補填',   description: '補填缺少慶佑倉資料的紀錄',        destructive: true },
  { value: 'sc_recalc',        label: 'SC 重算',      description: '重新計算銷售合約金額',             destructive: true },
];

const formatNumber = (v: number | null | undefined): string =>
  v == null ? '0' : v.toLocaleString('en-US', { maximumFractionDigits: 2 });

const C901GForm: React.FC = () => {
  const [category, setCategory] = useState<AuditCategory>('amount_check');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs('2004-01-01'),
    dayjs(),
  ]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RunAuditResponse | null>(null);

  const isDestructive = CATEGORY_OPTIONS.find(c => c.value === category)?.destructive ?? false;
  const isBlankKeyMode = category === 'blank_key_check';

  const handleRun = useCallback(async () => {
    setLoading(true);
    setResult(null);
    try {
      const req: RunAuditRequest = {
        category,
        dateFrom: dateRange[0].startOf('day').toISOString(),
        dateTo: dateRange[1].endOf('day').toISOString(),
      };
      const res = await c901gApi.runAudit(req);
      setResult(res);
      if (res.issuesFound === 0) {
        message.success('稽核完成，未發現異常');
      } else {
        message.warning(`稽核完成，發現 ${res.issuesFound} 筆異常`);
      }
    } catch (err: unknown) {
      const errMsg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '稽核執行失敗');
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [category, dateRange]);

  const elapsedSeconds = useMemo(() => {
    if (!result?.startedAt || !result?.completedAt) return null;
    const ms = new Date(result.completedAt).getTime() - new Date(result.startedAt).getTime();
    return (ms / 1000).toFixed(1);
  }, [result]);

  const amountColumns: ColumnsType<AuditResult> = useMemo(() => [
    { title: '序號', dataIndex: 'seq', key: 'seq', width: 70, sorter: (a, b) => a.seq - b.seq },
    { title: '單據編號', dataIndex: 'docNo', key: 'docNo', width: 140, sorter: (a, b) => a.docNo.localeCompare(b.docNo) },
    {
      title: '資料表', dataIndex: 'tableCode', key: 'tableCode', width: 100,
      render: (v: string) => <Tag color="blue">{v}</Tag>,
      filters: result
        ? Array.from(new Set(result.results.map(r => r.tableCode))).map(t => ({ text: t, value: t }))
        : [],
      onFilter: (value, record) => record.tableCode === value,
    },
    { title: '類型', dataIndex: 'docType', key: 'docType', width: 100 },
    {
      title: '單據日期', dataIndex: 'docDate', key: 'docDate', width: 110,
      render: (v: string | null) => v ? v.substring(0, 10) : '-',
      sorter: (a, b) => (a.docDate || '').localeCompare(b.docDate || ''),
    },
    {
      title: '主表金額', dataIndex: 'masterAmt', key: 'masterAmt', width: 120, align: 'right' as const,
      render: (v: number) => formatNumber(v),
      sorter: (a, b) => a.masterAmt - b.masterAmt,
    },
    {
      title: '重算金額', dataIndex: 'recalcAmt', key: 'recalcAmt', width: 120, align: 'right' as const,
      render: (v: number) => formatNumber(v),
      sorter: (a, b) => a.recalcAmt - b.recalcAmt,
    },
    {
      title: '差異', key: 'diff', width: 120, align: 'right' as const,
      render: (_: unknown, record: AuditResult) => {
        const diff = record.masterAmt - record.recalcAmt;
        if (diff === 0) return <span className="diff-zero">0</span>;
        const cls = diff > 0 ? 'diff-positive' : 'diff-negative';
        return <span className={cls}>{formatNumber(diff)}</span>;
      },
      sorter: (a, b) => (a.masterAmt - a.recalcAmt) - (b.masterAmt - b.recalcAmt),
    },
    { title: '建立者', dataIndex: 'creator', key: 'creator', width: 90 },
    { title: '狀態', dataIndex: 'status', key: 'status', width: 70 },
  ], [result]);

  const blankKeyColumns: ColumnsType<AuditResult> = useMemo(() => [
    { title: '序號', dataIndex: 'seq', key: 'seq', width: 70, sorter: (a, b) => a.seq - b.seq },
    {
      title: '資料表', dataIndex: 'tableCode', key: 'tableCode', width: 120,
      render: (v: string) => <Tag color="blue">{v}</Tag>,
      filters: result
        ? Array.from(new Set(result.results.map(r => r.tableCode))).map(t => ({ text: t, value: t }))
        : [],
      onFilter: (value, record) => record.tableCode === value,
    },
    { title: '類型', dataIndex: 'docType', key: 'docType', width: 120 },
    { title: '主鍵值', dataIndex: 'docNo', key: 'docNo', width: 200 },
    { title: '建立者', dataIndex: 'creator', key: 'creator', width: 100 },
    { title: '狀態', dataIndex: 'status', key: 'status', width: 80 },
  ], [result]);

  const columns = isBlankKeyMode ? blankKeyColumns : amountColumns;

  const runButton = (
    <Button
      type="primary"
      icon={<ThunderboltOutlined />}
      size="large"
      loading={loading}
      onClick={isDestructive ? undefined : handleRun}
      style={{ marginTop: 16 }}
    >
      {loading ? '稽核執行中...' : '執行稽核'}
    </Button>
  );

  return (
    <div className="c901g-form">
      <h2 style={{ marginBottom: 16 }}>
        <AuditOutlined style={{ marginRight: 8 }} />
        銷售稽核工具
      </h2>

      <div className="config-section">
        <Card title="稽核類別" size="small">
          <Radio.Group
            className="category-radio"
            value={category}
            onChange={e => { setCategory(e.target.value); setResult(null); }}
          >
            {CATEGORY_OPTIONS.map(opt => (
              <Radio key={opt.value} value={opt.value}>
                {opt.label}
                <span style={{ color: '#8c8c8c', fontSize: 12, marginLeft: 8 }}>
                  {opt.description}
                </span>
                {opt.destructive && (
                  <Tag color="red" style={{ marginLeft: 8, fontSize: 11 }}>修改操作</Tag>
                )}
              </Radio>
            ))}
          </Radio.Group>
        </Card>

        <Card title="執行設定" size="small">
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>日期範圍</label>
            <DatePicker.RangePicker
              value={dateRange}
              onChange={v => { if (v && v[0] && v[1]) setDateRange([v[0], v[1]]); }}
              style={{ width: '100%' }}
            />
          </div>

          {isDestructive ? (
            <Popconfirm
              title="確認執行"
              description={`「${CATEGORY_OPTIONS.find(c => c.value === category)?.label}」會修改資料，確定要執行嗎？`}
              onConfirm={handleRun}
              okText="確定執行"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              {runButton}
            </Popconfirm>
          ) : (
            runButton
          )}
        </Card>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: '#8c8c8c' }}>正在執行稽核，請稍候...</div>
        </div>
      )}

      {result && !loading && (
        <>
          <div className="stats-row" style={{ marginTop: 16 }}>
            <Card size="small"><Statistic title="檢查筆數" value={result.totalChecked} /></Card>
            <Card size="small">
              <Statistic
                title="異常筆數"
                value={result.issuesFound}
                valueStyle={{ color: result.issuesFound > 0 ? '#cf1322' : '#389e0d' }}
              />
            </Card>
            {elapsedSeconds && (
              <Card size="small"><Statistic title="耗時 (秒)" value={elapsedSeconds} /></Card>
            )}
          </div>

          {result.issuesFound === 0 ? (
            <Alert type="success" message="稽核完成，所有資料正常，未發現異常。" showIcon style={{ marginBottom: 16 }} />
          ) : (
            <Card title="稽核結果" size="small" style={{ marginTop: 0 }}>
              <Table<AuditResult>
                columns={columns}
                dataSource={result.results}
                rowKey="seq"
                size="small"
                scroll={{ x: isBlankKeyMode ? 700 : 1100 }}
                pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default C901GForm;
